/**
 * WidgetPanel — the in-session widget surface (task #4).
 *
 * A button on the session info bar that opens a popover. Widget *suggestion* is
 * decoupled from the chat agent: the parent fires the standalone Haiku endpoint
 * (api.suggestWidgets) in parallel with each send and passes the result here.
 * When a fresh result arrives the button pulses (color, no shake on web) to draw
 * attention; opening the panel clears it. Tapping an option SENDS A TEXT into the
 * session (carrying the typed #widget_<kind>_<id> tag) — the chat agent then logs
 * reactively. The panel is purely a launcher; it never writes data itself.
 *
 * Props:
 *  - suggest: { suggestions, logged_in_session, recent } | null
 *  - attention: bool — a fresh suggestion arrived and hasn't been seen
 *  - onSeen(): clear the attention flag (called on open)
 *  - onAction(text): send `text` into the session (parent's handleSend)
 *  - loading: bool — a suggest fetch is in flight
 */
import { useEffect, useRef, useState } from 'react';

const C = {
  bgCard: '#ffffff', bgHover: '#f2f2f5', border: '#d8d8dc',
  text: '#1d1d1f', textDim: '#6e6e73', accent: '#5856d6', accentDim: '#eeedf5',
  green: '#34c759', yellow: '#ff9500',
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
};

const GRID_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" fill="currentColor" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" fill="currentColor" opacity="0.55" />
  </svg>
);

const DEFAULT_ICON = '/default-widget-icon.svg';

function iconSrc(svg, url) {
  // Prefer a per-instance inline SVG (data: URI — img-svg can't run scripts, so
  // it's safe), else the host-served widget icon URL, else the generic default.
  if (typeof svg === 'string' && svg.trim().toLowerCase().startsWith('<svg')) {
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  }
  return url || DEFAULT_ICON;
}

// A widget/instance icon (inline SVG or host-served svg/png). Falls back to the
// generic default if absent or it fails — so a tile/row never shows a broken img.
export function WidgetIcon({ svg, url, size = 28, radius = 8 }) {
  const [src, setSrc] = useState(() => iconSrc(svg, url));
  useEffect(() => { setSrc(iconSrc(svg, url)); }, [svg, url]);
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      onError={() => { if (src !== DEFAULT_ICON) setSrc(DEFAULT_ICON); }}
      style={{
        width: size, height: size, borderRadius: radius, objectFit: 'cover',
        display: 'block', flexShrink: 0, background: '#f2f2f5',
      }}
    />
  );
}

// "My Widgets" — larger icons with small text below each (a wrap of icon tiles).
function WidgetTileRow({ items, onItem }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 8px 8px' }}>
      {items.map((it) => (
        <button
          key={it.instance_id || it.widget_ref}
          onClick={(e) => { e.stopPropagation(); onItem(it); }}
          title={`Log to ${it.title}`}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
            width: 64, padding: '4px 2px', border: 'none', background: 'transparent',
            cursor: 'pointer', fontFamily: C.font,
          }}
        >
          <WidgetIcon svg={it.icon_svg} url={it.icon_url} size={46} radius={12} />
          <span style={{
            fontSize: 10.5, color: C.text, lineHeight: 1.2, textAlign: 'center',
            maxWidth: 62, overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>{it.title}</span>
        </button>
      ))}
    </div>
  );
}

// The text a tapped option sends into the session. The #widget_<kind>_<id> tag
// is the deterministic part the agent resolves; the leading sentence is human
// context. The server now composes this PER ITEM (`action_text`) so the agent
// logs the RIGHT thing ("Log the iced latte to …", not just "this capture") —
// prefer it; fall back to the legacy widget-only phrasing for back-compat.
function actionText(opt) {
  if (opt.action_text && `${opt.action_text}`.trim()) return opt.action_text;
  const title = (opt.title || '').trim();
  if (opt.action === 'create') {
    if (opt.is_collection) {
      return `Create a new folder called "${title}" and log this capture into it ${opt.tag}`;
    }
    return `Track this capture with the "${title}" widget ${opt.tag}`;
  }
  return `Log this capture to "${title}" ${opt.tag}`;
}

// "Log <item> to <name>" / "Create <name> Folder" / "Track with <name> Widget".
// Prefer the server-composed item-aware `label`; fall back to widget-only text.
function optionLabel(opt) {
  if (opt.label && `${opt.label}`.trim()) return opt.label;
  const title = (opt.title || '').trim();
  if (opt.action === 'create') {
    if (opt.is_collection) return `Create "${title}" Folder`;
    return `Track with "${title}" Widget`;
  }
  return `Log to ${title}`;
}

// Breathing "Queuing" pill — the option's log is in flight: its status-track
// log card is published but uncommitted (server `status:queuing`), or it was
// just tapped (client optimistic overlay), or its committed row was deleted so
// the card is "canceled" and the option is queuing AGAIN (A1.5, `status:canceled`).
function QueuingBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
      padding: '2px 8px', borderRadius: 10, background: C.accentDim,
      color: C.accent, fontSize: 10, fontWeight: 600,
      animation: 'wp-breathe 1.2s ease-in-out infinite',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: C.accent }} />
      Queuing
    </span>
  );
}

// ✓ pill — the option's log card committed a row (server `status:logged`). The
// option becomes a badge; tapping it jumps to the bound log card (never re-logs).
function LoggedBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
      padding: '2px 8px', borderRadius: 10, background: '#e8f7ec',
      color: C.green, fontSize: 10, fontWeight: 700,
    }}>
      ✓ Logged
    </span>
  );
}

// The small per-option preview thumbnail (G1 / A1.6 / A5) — the image(s) THIS
// option would log. Two near-identical options ("Log to Nutritionist" for two
// dishes) are told apart by their own thumbnail + subtext. Falls back to the
// widget glyph when the option carries no media (refresh-mode / text-only).
function OptionThumb({ opt }) {
  const [ok, setOk] = useState(true);
  const isCreate = opt.action === 'create';
  const isCollection = !!opt.is_collection;
  if (opt.media_thumb && ok) {
    return (
      <img
        src={opt.media_thumb}
        alt=""
        aria-hidden="true"
        onError={() => setOk(false)}
        style={{
          width: 30, height: 30, borderRadius: 7, objectFit: 'cover',
          display: 'block', flexShrink: 0, marginTop: 1, background: '#f2f2f5',
          border: `1px solid ${C.border}`,
        }}
      />
    );
  }
  if (opt.icon_url || opt.icon_svg) {
    return (
      <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}>
        <WidgetIcon svg={opt.icon_svg} url={opt.icon_url} size={28} radius={7} />
      </span>
    );
  }
  return (
    <span style={{
      flexShrink: 0, width: 28, height: 28, borderRadius: 7, marginTop: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isCreate ? 'rgba(52,199,89,0.14)' : C.accentDim,
      color: isCreate ? C.green : C.accent, fontSize: 14, fontWeight: 700,
    }}>{isCollection ? '▦' : isCreate ? '＋' : '↳'}</span>
  );
}

// The canonical per-option state machine (findings §6.4 — identical web + app):
//   idle     → tappable; tap sends action_text + attaches THIS option's media_refs.
//   queuing  → "Queuing" badge, re-send disabled; tap → jump to log_card_id card.
//   logged   → ✓ badge; tap → jump to log_card_id card (never re-logs).
//   canceled → "Queuing" badge again (A1.5); tap → jump to the canceled card.
// `status` is server-authoritative; an optimistic local overlay (set on idle
// tap) is folded in by the parent before render. `onLog`/`onJump` are split so
// the row can either dispatch a send (idle) or jump to its bound card.
function OptionRow({ opt, onLog, onJump }) {
  const [hover, setHover] = useState(false);
  const status = opt.status || (opt.queuing ? 'queuing' : 'idle');
  const isQueuing = status === 'queuing' || status === 'canceled';
  const isLogged = status === 'logged';
  // idle → dispatch the send (with this option's media); queuing/logged/canceled
  // → jump to the bound status-track card (re-log only via the card, never here).
  const hasCard = !!opt.log_card_id;
  const tappable = status === 'idle' || hasCard;
  const onRowClick = () => {
    if (status === 'idle') onLog(opt);
    else if (hasCard) onJump(opt.log_card_id);
  };
  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (tappable) onRowClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={!tappable}
      title={
        isLogged ? `Jump to the “${opt.title}” log card`
          : isQueuing ? `“${opt.title}” log is queuing — open its card to finish`
            : optionLabel(opt)
      }
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%',
        textAlign: 'left', padding: '8px 10px', border: 'none',
        cursor: tappable ? 'pointer' : 'default',
        borderRadius: 8, background: tappable && hover ? C.bgHover : 'transparent', fontFamily: C.font,
      }}
    >
      <OptionThumb opt={opt} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.text }}>
          {optionLabel(opt)}
        </span>
        {/* Per-option subtext (G1) — the dish / what this option is, so two
            similar options are distinguishable. Prefer the item descriptor,
            fall back to the classifier's reason. */}
        {(opt.item || opt.reason) && (
          <span style={{ display: 'block', fontSize: 11, color: C.textDim, marginTop: 1, lineHeight: 1.3 }}>
            {opt.item || opt.reason}
          </span>
        )}
      </span>
      {isLogged ? <LoggedBadge /> : isQueuing ? <QueuingBadge /> : null}
    </button>
  );
}

function ChipRow({ items, onItem, logged }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '2px 4px 6px' }}>
      {items.map((it) => {
        // status "pending": the log card is published but the user hasn't
        // committed a portion/option yet → breathing dot instead of the ✓;
        // clicking still jumps to the card to finish the commit.
        const pending = logged && it.status === 'pending';
        // A4 — the widget instance was hard-deleted; the bound log card is now
        // "canceled — widget deleted". Render a muted chip (no ✓); clicking still
        // jumps to the card, which offers recreate ("Use this Widget" / Log).
        const deleted = !!it.widget_deleted;
        const canceled = logged && it.status === 'canceled';
        return (
          <button
            key={it.log_card_id || it.instance_id}
            onClick={(e) => { e.stopPropagation(); onItem(it); }}
            style={{
              fontSize: 11, padding: '4px 9px', borderRadius: 14, cursor: 'pointer',
              border: `1px solid ${pending ? C.accent : C.border}`,
              background: deleted ? '#faf6f6' : logged ? '#f0f0f4' : C.bgCard,
              color: deleted ? C.textDim : C.text, fontFamily: C.font, maxWidth: 200, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              borderStyle: deleted ? 'dashed' : 'solid',
              animation: pending ? 'wp-breathe 1.2s ease-in-out infinite' : 'none',
            }}
            title={deleted
              ? `“${it.title}” was deleted — open the card to recreate`
              : pending
                ? `“${it.title}” log is queuing — open its card to finish`
                : canceled
                  ? `“${it.title}” log was canceled — open its card to re-log`
                  : logged ? `Jump to the “${it.title}” log card` : `Log to ${it.title}`}
          >
            {pending && <span style={{ width: 6, height: 6, borderRadius: 3, background: C.accent, flexShrink: 0 }} />}
            {deleted
              ? `${it.title} · widget deleted`
              : canceled ? it.title : pending ? it.title : logged ? `✓ ${it.title}` : it.title}
          </button>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      color: C.textDim, padding: '8px 10px 4px',
    }}>{children}</div>
  );
}

function SkelBar({ w, h, r = 8 }) {
  return <div style={{ width: w, height: h, borderRadius: r, background: '#ececf0', flexShrink: 0 }} />;
}

// A neutral skeleton for the FIRST load (nothing cached) — mirrors the
// always-present sections so the lazy fill-in is smooth, not a text flash.
function PanelSkeleton() {
  return (
    <div>
      <SectionLabel>Suggestions to log</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '6px 10px 8px' }}>
        <SkelBar w="100%" h={16} />
        <SkelBar w="64%" h={16} />
      </div>
      <SectionLabel>Log to my widgets</SectionLabel>
      <div style={{ display: 'flex', gap: 8, padding: '4px 10px 8px' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, width: 64 }}>
            <SkelBar w={46} h={46} r={12} />
            <SkelBar w={42} h={9} r={4} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WidgetPanel({ suggest, attention, onSeen, onOpen, onAction, onJumpToCard, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Phase 13: a "Track with Widgets" button elsewhere (e.g. on a
  // user_questions_prediction card) can pull this panel up via a window event —
  // pure client-side, no server involvement. Mirrors the toggle's open path.
  useEffect(() => {
    const onOpenEvt = () => {
      if (!open) {
        if (attention) onSeen?.();
        onOpen?.();
      }
      setOpen(true);
    };
    window.addEventListener('vi-open-widget-panel', onOpenEvt);
    return () => window.removeEventListener('vi-open-widget-panel', onOpenEvt);
  }, [open, attention, onSeen, onOpen]);

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next) {
      if (attention) onSeen?.();
      // Refresh the deterministic sections (logged-this-session + your widgets)
      // every time the panel opens, so it's never stale/empty after a log.
      onOpen?.();
    }
  };

  // A send into the session. `mediaRefs` lets a content option carry ITS OWN
  // images (per-image binding); `optionId` lets the parent set an optimistic
  // queuing overlay on exactly that option until the server's status lands.
  const act = (text, mediaRefs, optionId) => { onAction?.(text, mediaRefs, optionId); setOpen(false); };
  // Tapping an idle content option → send its action_text AND attach THIS
  // option's media_refs (the per-image binding, G1) so two options bound to two
  // different images each log their own image. Empty media_refs → parent falls
  // back to the round's capture media. Pass the full `id` so the optimistic
  // overlay is scoped to this exact per-image option.
  const onLogOption = (opt) => act(
    actionText(opt),
    Array.isArray(opt.media_refs) ? opt.media_refs : undefined,
    opt.id || null,
  );
  const onJump = (lcid) => { if (lcid) { setOpen(false); onJumpToCard?.(lcid); } };

  const suggestions = suggest?.suggestions || [];
  const logged = suggest?.logged_in_session || [];
  const recent = suggest?.recent || [];
  const hasAny = suggestions.length || logged.length || recent.length;
  // Badge counts only ACTIVE logs — canceled / widget-deleted cards are surfaced in the
  // section (A1.5 "queuing again" / A4 "widget deleted") but must not inflate the count.
  const loggedCount = logged.filter((e) => e.status !== 'canceled' && !e.widget_deleted).length;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <style>{`@keyframes wp-attn {
        0%,100% { box-shadow: 0 0 0 0 rgba(88,86,214,0.0); background:${C.accentDim}; }
        50% { box-shadow: 0 0 0 4px rgba(88,86,214,0.18); background:#dcd9f7; }
      }
      @keyframes wp-breathe { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      <button
        onClick={toggle}
        title="Widgets for this capture"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
          fontSize: 10, fontFamily: C.font,
          border: `1px solid ${attention ? 'transparent' : C.border}`,
          color: attention ? C.accent : C.textDim,
          background: attention ? C.accentDim : 'transparent',
          animation: attention ? 'wp-attn 1.4s ease-in-out infinite' : 'none',
          transition: 'color 0.2s, background 0.2s',
        }}
      >
        {GRID_ICON}
        <span>Widgets</span>
        {/* Badge = number of ACTIVE log cards in this session (0 → hidden; excludes canceled/deleted). */}
        {loggedCount > 0 && (
          <span style={{
            minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7,
            background: attention ? C.accent : C.textDim, color: '#fff',
            fontSize: 9, fontWeight: 700, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
          }}>{loggedCount}</span>
        )}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 28, right: 0, zIndex: 30, width: 290,
            maxHeight: 420, overflowY: 'auto', background: C.bgCard,
            border: `1px solid ${C.border}`, borderRadius: 12, padding: 4,
            boxShadow: '0 8px 28px rgba(0,0,0,0.14)', fontFamily: C.font,
          }}
        >
          {logged.length > 0 && (
            <>
              <SectionLabel>Logged in this session</SectionLabel>
              <ChipRow
                items={logged}
                logged
                onItem={(it) => { setOpen(false); onJumpToCard?.(it.log_card_id); }}
              />
            </>
          )}

          {suggestions.length > 0 && (
            <>
              <SectionLabel>Suggestions to log</SectionLabel>
              {suggestions.map((opt) => (
                // key by the FULL `id` — it now folds the media key
                // ({action}:{widget_ref}:{norm(item)}:{media_key}) so two
                // same-widget per-image options stay distinct React rows. The
                // option's canonical `status` (idle/queuing/logged/canceled),
                // already merged with the parent's optimistic overlay, drives
                // the row's render + tap behaviour.
                <OptionRow
                  key={opt.id || opt.widget_ref}
                  opt={opt}
                  onLog={onLogOption}
                  onJump={onJump}
                />
              ))}
            </>
          )}

          {recent.length > 0 && (
            <>
              <SectionLabel>Log to my widgets</SectionLabel>
              <WidgetTileRow
                items={recent}
                onItem={(it) => act(`Log this capture to "${it.title}" ${it.tag}`)}
              />
            </>
          )}

          {!hasAny && loading && <PanelSkeleton />}
          {!hasAny && !loading && (
            <div style={{ padding: '14px 12px', fontSize: 12, color: C.textDim, textAlign: 'center' }}>
              No widgets yet — start one from a capture.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
